import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@shared/hooks/useDebounce';
import { listSubscriptionApiService } from '../api/listSubscriptionApiService';
import type { SubscriptionListItem } from '../types/subscriptionListType';
import type { FilterOption } from '@shared/components/FilterPopup';

const BILLING_CYCLE_OPTIONS: FilterOption[] = [
  { label: 'Miesięczny', value: 'MONTHLY', checked: false },
  { label: 'Kwartalny', value: 'QUARTERLY', checked: false },
  { label: 'Roczny', value: 'YEARLY', checked: false },
];

// Validation utilities
const isValidBillingCycle = (value: string): boolean => {
  return value === 'MONTHLY' || value === 'QUARTERLY' || value === 'YEARLY';
};

const sanitizeBillingCycleString = (
  str: string | null | undefined
): string | undefined => {
  if (!str) {
    return undefined;
  }
  const values = str
    .split(',')
    .map((s) => s.trim().toUpperCase())
    .filter((s) => isValidBillingCycle(s));
  return values.length > 0 ? values.join(',') : undefined;
};

const isValidStatus = (status: string): boolean => {
  const validStatuses = ['active', 'inactive'];
  return validStatuses.includes(status);
};

const validateStatusString = (statusStr: string): boolean => {
  if (!statusStr) {
    return true;
  }
  const statuses = statusStr.split(',');
  return statuses.every((s) => isValidStatus(s.trim()));
};

// Sanitize status string - validates and filters invalid statuses
const sanitizeStatusString = (
  statusStr: string | null | undefined
): string | undefined => {
  if (!statusStr) {
    return undefined;
  }
  const trimmed = String(statusStr).trim();
  if (!trimmed) {
    return undefined;
  }

  const statuses = trimmed
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s);
  const validStatuses = statuses.filter((s) => isValidStatus(s));

  return validStatuses.length > 0 ? validStatuses.join(',') : undefined;
};

// Parse status URL param into list so we match whole values (e.g. "inactive" must not match "active")
const statusListFromParam = (
  statusStr: string | null | undefined
): string[] => {
  if (!statusStr) {
    return [];
  }
  return statusStr
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s);
};

export const useSubscriptionList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isSyncingFromUrl = useRef(false);

  // Initialize from URL params or storage
  const urlPage = searchParams.get('page');
  const urlSearch = searchParams.get('search');
  const urlLimit = searchParams.get('limit');
  const urlBillingCycle = sanitizeBillingCycleString(
    searchParams.get('billingCycle')
  );
  const urlStatus = sanitizeStatusString(searchParams.get('status'));

  const isValidUrlStatus = validateStatusString(urlStatus || '');

  const [subscriptions, setSubscriptions] = useState<SubscriptionListItem[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(() => {
    const urlPageValue = urlPage ? parseInt(urlPage, 10) : 1;
    // Validate page - must be at least 1
    return urlPageValue < 1 ? 1 : urlPageValue;
  });
  const [limit, setLimit] = useState(() => {
    const urlLimitValue = urlLimit ? parseInt(urlLimit, 10) : 10;
    // Validate limit on initialization - max 100, min 1
    return urlLimitValue > 100 ? 10 : urlLimitValue < 1 ? 10 : urlLimitValue;
  });
  const [totalCount, setTotalCount] = useState(0);
  const [isNoFilterFetch, setIsNoFilterFetch] = useState(false); // Track if current data is from no-filter fetch
  const [search, setSearch] = useState(() => {
    // Validate search on initialization - max 200 characters, trim whitespace
    const searchValue = (urlSearch || '').trim();
    return searchValue.length > 200
      ? searchValue.substring(0, 200)
      : searchValue;
  });
  const [limitError, setLimitError] = useState<string>('');
  const debouncedSearch = useDebounce(search, 500);
  const prevSearchRef = useRef<string>(search);
  const prevDebouncedSearchRef = useRef<string>(debouncedSearch);

  // Initialize billingCycleOptions from URL
  const [billingCycleOptions, setBillingCycleOptions] = useState<
    FilterOption[]
  >(() => {
    if (!urlBillingCycle) {
      return BILLING_CYCLE_OPTIONS.map((opt) => ({ ...opt }));
    }
    const values = urlBillingCycle.split(',');
    return BILLING_CYCLE_OPTIONS.map((opt) => ({
      ...opt,
      checked: values.includes(opt.value),
    }));
  });

  // Initialize statusOptions with validation
  const [statusOptions, setStatusOptions] = useState<FilterOption[]>(() => {
    const statusList = statusListFromParam(urlStatus ?? null);
    if (urlStatus && !isValidUrlStatus) {
      return [
        { label: 'Aktywny', value: 'active', checked: false },
        { label: 'Nieaktywny', value: 'inactive', checked: false },
      ];
    }
    if (urlStatus) {
      return [
        {
          label: 'Aktywny',
          value: 'active',
          checked: statusList.includes('active'),
        },
        {
          label: 'Nieaktywny',
          value: 'inactive',
          checked: statusList.includes('inactive'),
        },
      ];
    }
    return [
      { label: 'Aktywny', value: 'active', checked: false },
      { label: 'Nieaktywny', value: 'inactive', checked: false },
    ];
  });

  // Initialize appliedFilters with validation
  const [appliedFilters, setAppliedFilters] = useState<{
    billingCycleOptions: FilterOption[];
    statusOptions: FilterOption[];
  }>(() => {
    if (urlStatus && !isValidUrlStatus) {
      return {
        billingCycleOptions: BILLING_CYCLE_OPTIONS.map((opt) => ({ ...opt })),
        statusOptions: [
          { label: 'Aktywny', value: 'active', checked: false },
          { label: 'Nieaktywny', value: 'inactive', checked: false },
        ],
      };
    }
    const statusList = statusListFromParam(urlStatus ?? null);
    return {
      billingCycleOptions: urlBillingCycle
        ? BILLING_CYCLE_OPTIONS.map((opt) => ({
            ...opt,
            checked: urlBillingCycle.split(',').includes(opt.value),
          }))
        : BILLING_CYCLE_OPTIONS.map((opt) => ({ ...opt })),
      statusOptions: urlStatus
        ? [
            {
              label: 'Aktywny',
              value: 'active',
              checked: statusList.includes('active'),
            },
            {
              label: 'Nieaktywny',
              value: 'inactive',
              checked: statusList.includes('inactive'),
            },
          ]
        : [
            { label: 'Aktywny', value: 'active', checked: false },
            { label: 'Nieaktywny', value: 'inactive', checked: false },
          ],
    };
  });

  // Sync state from URL params when URL changes (e.g., browser back/forward)
  useEffect(() => {
    isSyncingFromUrl.current = true;
    const currentUrlPage = searchParams.get('page');
    const currentUrlSearch = searchParams.get('search');
    const currentUrlLimit = searchParams.get('limit');
    const currentUrlBillingCycle = sanitizeBillingCycleString(
      searchParams.get('billingCycle')
    );
    const currentUrlStatus = sanitizeStatusString(searchParams.get('status'));

    if (currentUrlPage) {
      const urlPageValue = parseInt(currentUrlPage, 10);
      if (urlPageValue >= 1 && urlPageValue !== page) {
        setPage(urlPageValue);
      }
    } else if (page !== 1) {
      setPage(1);
    }

    if (currentUrlLimit) {
      const urlLimitValue = parseInt(currentUrlLimit, 10);
      if (
        urlLimitValue >= 1 &&
        urlLimitValue <= 100 &&
        urlLimitValue !== limit
      ) {
        setLimit(urlLimitValue);
      }
    }

    const urlSearchValue = (currentUrlSearch || '').trim();
    const currentSearchValue = search.trim();
    if (urlSearchValue !== currentSearchValue) {
      const validatedSearch =
        urlSearchValue.length > 200
          ? urlSearchValue.substring(0, 200)
          : urlSearchValue;
      setSearch(validatedSearch);
    }

    if (currentUrlBillingCycle) {
      const values = currentUrlBillingCycle.split(',');
      const urlBillingOptions = BILLING_CYCLE_OPTIONS.map((opt) => ({
        ...opt,
        checked: values.includes(opt.value),
      }));
      const billingChanged = urlBillingOptions.some(
        (opt, i) => opt.checked !== billingCycleOptions[i]?.checked
      );
      if (billingChanged) {
        setBillingCycleOptions(urlBillingOptions);
      }
    } else if (billingCycleOptions.some((opt) => opt.checked)) {
      setBillingCycleOptions(BILLING_CYCLE_OPTIONS.map((opt) => ({ ...opt })));
    }

    const isValidCurrentUrlStatus = validateStatusString(
      currentUrlStatus || ''
    );
    if (isValidCurrentUrlStatus && currentUrlStatus) {
      const currentStatusListForSync = statusListFromParam(currentUrlStatus);
      const urlStatusOptions: FilterOption[] = [
        {
          label: 'Aktywny',
          value: 'active',
          checked: currentStatusListForSync.includes('active'),
        },
        {
          label: 'Nieaktywny',
          value: 'inactive',
          checked: currentStatusListForSync.includes('inactive'),
        },
      ];
      const currentStatusOptions = statusOptions;
      if (
        urlStatusOptions[0]?.checked !== currentStatusOptions[0]?.checked ||
        urlStatusOptions[1]?.checked !== currentStatusOptions[1]?.checked
      ) {
        setStatusOptions(urlStatusOptions);
      }
    } else if (!currentUrlStatus && statusOptions.some((opt) => opt.checked)) {
      setStatusOptions([
        { label: 'Aktywny', value: 'active', checked: false },
        { label: 'Nieaktywny', value: 'inactive', checked: false },
      ]);
    }

    if (currentUrlBillingCycle || isValidCurrentUrlStatus) {
      const urlBillingOpts = currentUrlBillingCycle
        ? BILLING_CYCLE_OPTIONS.map((opt) => ({
            ...opt,
            checked: currentUrlBillingCycle.split(',').includes(opt.value),
          }))
        : BILLING_CYCLE_OPTIONS.map((opt) => ({ ...opt }));
      const currentStatusList = statusListFromParam(
        isValidCurrentUrlStatus && currentUrlStatus ? currentUrlStatus : null
      );
      const urlAppliedFilters = {
        billingCycleOptions: urlBillingOpts,
        statusOptions:
          isValidCurrentUrlStatus && currentUrlStatus
            ? [
                {
                  label: 'Aktywny',
                  value: 'active',
                  checked: currentStatusList.includes('active'),
                },
                {
                  label: 'Nieaktywny',
                  value: 'inactive',
                  checked: currentStatusList.includes('inactive'),
                },
              ]
            : [
                { label: 'Aktywny', value: 'active', checked: false },
                { label: 'Nieaktywny', value: 'inactive', checked: false },
              ],
      };
      const currentAppliedFilters = appliedFilters;
      const billingFiltersChanged = urlAppliedFilters.billingCycleOptions.some(
        (opt, i) =>
          opt.checked !== currentAppliedFilters.billingCycleOptions[i]?.checked
      );
      const statusFiltersChanged = urlAppliedFilters.statusOptions.some(
        (opt, i) =>
          opt.checked !== currentAppliedFilters.statusOptions[i]?.checked
      );
      if (billingFiltersChanged || statusFiltersChanged) {
        setAppliedFilters(urlAppliedFilters);
      }
    } else if (
      !currentUrlBillingCycle &&
      !currentUrlStatus &&
      (appliedFilters.billingCycleOptions.some((opt) => opt.checked) ||
        appliedFilters.statusOptions.some((opt) => opt.checked))
    ) {
      setAppliedFilters({
        billingCycleOptions: BILLING_CYCLE_OPTIONS.map((opt) => ({ ...opt })),
        statusOptions: [
          { label: 'Aktywny', value: 'active', checked: false },
          { label: 'Nieaktywny', value: 'inactive', checked: false },
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Reset sync flag after state updates complete
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      isSyncingFromUrl.current = false;
    }
  }, [page, limit, search, billingCycleOptions, statusOptions, appliedFilters]);

  // Sync URL params with state (moved after appliedFilters declaration)
  useEffect(() => {
    // Skip URL sync if we're currently syncing from URL to prevent infinite loop
    if (isSyncingFromUrl.current) {
      prevSearchRef.current = search;
      return;
    }

    const params = new URLSearchParams();

    // Check if search changed - if so, force page to 1
    const prevSearchTrimmed = prevSearchRef.current.trim();
    const currentSearchTrimmed = search.trim();
    const searchChanged = prevSearchTrimmed !== currentSearchTrimmed;

    if (searchChanged) {
      prevSearchRef.current = search;
      // Force page to 1 when search changes (both in URL and state)
      params.set('page', '1');
      // Also update page state if it's not already 1
      if (page !== 1) {
        setPage(1);
      }
    } else {
      // Use current page state
      params.set('page', page.toString());
    }

    params.set('limit', limit.toString());

    // Add search param if present
    if (search) {
      params.set('search', search);
    }

    // Add filter params to URL
    const selectedBillingCycles = appliedFilters.billingCycleOptions
      .filter((opt) => opt.checked)
      .map((opt) => opt.value)
      .join(',');
    if (selectedBillingCycles) {
      params.set('billingCycle', selectedBillingCycles);
    }
    const selectedStatuses = appliedFilters.statusOptions
      .filter((opt) => opt.checked)
      .map((opt) => opt.value)
      .join(',');
    if (selectedStatuses) {
      params.set('status', selectedStatuses);
    }

    // Check if URL params are different from current URL to avoid unnecessary updates
    const currentUrlParams = new URLSearchParams(searchParams.toString());
    const newUrlString = params.toString();
    const currentUrlString = currentUrlParams.toString();

    // Only update URL if it's different (prevents loop when syncing from URL)
    if (newUrlString !== currentUrlString) {
      setSearchParams(params, { replace: false });
    }
  }, [page, limit, search, appliedFilters, setSearchParams, searchParams]);

  const fetchSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      const selectedStatuses = appliedFilters.statusOptions
        .filter((opt) => opt.checked)
        .map((opt) => opt.value)
        .join(',');

      const selectedBillingCycles = appliedFilters.billingCycleOptions
        .filter((opt) => opt.checked)
        .map((opt) => opt.value)
        .join(',');

      const trimmedSearch = debouncedSearch.trim();

      if (selectedStatuses && !validateStatusString(selectedStatuses)) {
        setAppliedFilters({
          billingCycleOptions: appliedFilters.billingCycleOptions,
          statusOptions: [
            { label: 'Aktywny', value: 'active', checked: false },
            { label: 'Nieaktywny', value: 'inactive', checked: false },
          ],
        });
        setSubscriptions([]);
        setTotalCount(0);
        return;
      }

      const response = await listSubscriptionApiService.listSubscriptions({
        page,
        limit,
        ...(trimmedSearch && { search: trimmedSearch }),
        ...(selectedStatuses && { status: selectedStatuses }),
        ...(selectedBillingCycles && { billingCycle: selectedBillingCycles }),
      });
      setSubscriptions(response.data);
      setTotalCount(response.totalCount);

      const hasNoFilters =
        !trimmedSearch && !selectedStatuses && !selectedBillingCycles;
      setIsNoFilterFetch(hasNoFilters);
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
      setSubscriptions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch, appliedFilters]);

  // Reset page to 1 when debounced search changes (additional safeguard)
  // This ensures page is 1 before fetchSubscriptions is called
  useEffect(() => {
    // Skip reset if we're syncing from URL
    if (isSyncingFromUrl.current) {
      prevDebouncedSearchRef.current = debouncedSearch;
      return;
    }

    // Reset page to 1 when debounced search actually changes
    const prevDebouncedTrimmed = prevDebouncedSearchRef.current.trim();
    const currentDebouncedTrimmed = debouncedSearch.trim();

    if (prevDebouncedTrimmed !== currentDebouncedTrimmed) {
      prevDebouncedSearchRef.current = debouncedSearch;
      // Ensure page is 1 when search changes (before API call)
      if (page !== 1) {
        setPage(1);
      }
    }
  }, [debouncedSearch, page]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const handleLimitChange = useCallback((newLimit: number) => {
    if (newLimit > 100) {
      setLimitError('Limit nie może przekraczać 100.');
      return;
    }
    if (newLimit < 1) {
      setLimitError('Limit musi być większy niż 0.');
      return;
    }
    setLimitError('');
    setLimit(newLimit);
    setPage(1); // Reset to page 1 when limit changes
  }, []);

  const handleSearchChange = useCallback(
    (newSearch: string) => {
      // Enforce 200 character limit
      if (newSearch.length > 200) {
        return;
      }

      // Reset page to 1 when search value changes (unless syncing from URL)
      const currentSearchTrimmed = search.trim();
      const newSearchTrimmed = newSearch.trim();

      if (
        !isSyncingFromUrl.current &&
        currentSearchTrimmed !== newSearchTrimmed
      ) {
        // Reset page to 1 immediately when search changes
        // This ensures page is reset before URL sync happens
        setPage(1);
      }
      // Immediately show skeleton when clearing an active search so the empty
      // state never flickers during the 300ms debounce window.
      if (!newSearchTrimmed && currentSearchTrimmed) {
        setLoading(true);
      }

      setSearch(newSearch);
    },
    [search]
  );

  const handleApplyFilters = useCallback(() => {
    const selectedStatuses = statusOptions
      .filter((opt) => opt.checked)
      .map((opt) => opt.value)
      .join(',');

    if (selectedStatuses && !validateStatusString(selectedStatuses)) {
      return;
    }

    setAppliedFilters({ billingCycleOptions, statusOptions });
    setPage(1);
  }, [billingCycleOptions, statusOptions]);

  const handleClearFilters = useCallback(() => {
    const defaultBillingCycleOptions = BILLING_CYCLE_OPTIONS.map((opt) => ({
      ...opt,
    }));
    const defaultStatusOptions = [
      { label: 'Aktywny', value: 'active', checked: false },
      { label: 'Nieaktywny', value: 'inactive', checked: false },
    ];
    const defaultFilters = {
      billingCycleOptions: defaultBillingCycleOptions,
      statusOptions: defaultStatusOptions,
    };

    const hasAppliedFilters =
      appliedFilters.billingCycleOptions.some((opt) => opt.checked) ||
      appliedFilters.statusOptions.some((opt) => opt.checked);

    setBillingCycleOptions(defaultBillingCycleOptions);
    setStatusOptions(defaultStatusOptions);

    if (hasAppliedFilters) {
      setAppliedFilters(defaultFilters);
      setPage(1);
    }
  }, [appliedFilters]);

  const isSearchClearing =
    !search.trim() &&
    debouncedSearch.trim() !== '' &&
    !appliedFilters.billingCycleOptions.some((opt) => opt.checked) &&
    !appliedFilters.statusOptions.some((opt) => opt.checked);

  const prevDebouncedTrimmed = prevDebouncedSearchRef.current.trim();
  const currentDebouncedTrimmed = debouncedSearch.trim();
  const justClearedSearch =
    prevDebouncedTrimmed !== '' &&
    currentDebouncedTrimmed === '' &&
    !appliedFilters.billingCycleOptions.some((opt) => opt.checked) &&
    !appliedFilters.statusOptions.some((opt) => opt.checked);

  return {
    subscriptions,
    loading,
    page,
    setPage,
    limit,
    setLimit,
    totalCount,
    isNoFilterFetch,
    search,
    debouncedSearch,
    isSearchClearing: isSearchClearing || justClearedSearch,
    setSearch: handleSearchChange,
    billingCycleOptions,
    setBillingCycleOptions,
    statusOptions,
    setStatusOptions,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    refetchSubscriptions: fetchSubscriptions,
  };
};
